import React, { useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { LinePath, AreaClosed } from '@visx/shape';
import { curveLinear } from '@visx/curve';
import { AxisBottom, AxisRight } from '@visx/axis';
import { Box, useTheme, CircularProgress } from '@mui/material';
import HoverOverlay from './HoverOverlay';
import type { GeoJsonFeature } from '../../utils/ogc';
import type { MaxPrecipDataFeatureProperties } from '../../layers/max_precip_data';
import { depthToColor } from '../../utils/layer_style';
import { format } from 'date-fns';

type MiniPoint = { t: Date; d: number; label?: string };

const getProps = (f: GeoJsonFeature) => f.properties ?? {};
const getDeploymentId = (f: GeoJsonFeature) => getProps(f).deployment_id ?? getProps(f).id ?? '';
const getLabel = (f: GeoJsonFeature) => getProps(f).name ?? getDeploymentId(f);
const getTimes = (f: GeoJsonFeature) => getProps(f).time ?? [];
const getDepths = (f: GeoJsonFeature) => getProps(f).depth_inches ?? [];
const getPastEvents = (f: GeoJsonFeature) => getProps(f).past_events ?? [];
const getLabels = (f: GeoJsonFeature) => (getProps(f) as any)?.labels ?? (getProps(f) as any)?.label ?? [];

const MAX_GAP_MS = 60 * 60 * 1000;
const MIN_CHART_WIDTH = 200;
const PRECIP_BAR_PADDING = 0.8;
const PRECIP_SOFT_MIN = 0.1;
const DEFAULT_PALETTE = ['#0e9089'];

export type Series = { key: string; label: string; data: MiniPoint[]; feature: GeoJsonFeature };

function MiniDepthChart({
  timeRange,
  currentTime,
  features,
  precip,
  loading = false,
  highlightedKey,
  onClick,
  unit = 'in',
  height = 36,
  caption,
  overlayCaptureHandlers,
  overlayClickSuppressRef,
  depthSoftMinInches = 6,
}: {
  timeRange: [Date, Date, boolean];
  currentTime: Date | undefined;
  features: GeoJsonFeature[];
  precip?: MaxPrecipDataFeatureProperties[] | undefined;
  loading?: boolean; highlightedKey?: string | null,
  onClick?: (feature: GeoJsonFeature) => void; unit?: string; height?: number;
  caption?: string;
  overlayCaptureHandlers?: { onMouseDownCapture?: (e: React.MouseEvent<SVGRectElement>) => void; onMouseMoveCapture?: (e: React.MouseEvent<SVGRectElement>) => void; onMouseUpCapture?: (e: React.MouseEvent<SVGRectElement>) => void }; overlayClickSuppressRef?: React.MutableRefObject<boolean | undefined>
  depthSoftMinInches?: number;
}) {
  const [fromTime, toTime] = timeRange;
  const theme = useTheme();
  const textColorPrimary = theme.palette.text.primary;
  const textColorSecondary = theme.palette.text.secondary;
  const fromTimeStr = useMemo(() => format(fromTime, 'PP p'), [fromTime]);
  const toTimeStr = useMemo(() => format(toTime, 'PP p'), [toTime]);

  const { containerRef, width } = useChartWidth();
  const h = height;
  const w = width;
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const [showLoading, setShowLoading] = React.useState(false);
  React.useEffect(() => {
    if (!loading) { setShowLoading(false); return; }
    const id = window.setTimeout(() => setShowLoading(true), 1500);
    return () => window.clearTimeout(id);
  }, [loading]);

  const series = useSeries(features, fromTime, toTime);
  // console.log(series, features);
  const showEventBands = !series.length;

  const yMax = useYMax(series, features, depthSoftMinInches, showEventBands);
  const xScale = React.useMemo(() => scaleTime<number>({ domain: [fromTime, toTime], range: [0, w] }), [fromTime, toTime, w]);
  const yScale = React.useMemo(() => scaleLinear<number>({ domain: [0, yMax], range: [h - 1, 0], nice: true }), [yMax, h]);

  const now = new Date();
  const nowX = xScale(now);

  const tidePts = useTidePoints(null, fromTime, toTime);
  const { bars, precipMax } = usePrecipBars(precip, fromTime, toTime, xScale, h);
  const eventBands = useEventBands(showEventBands, features, fromTime, toTime, xScale, yScale);
  const noDepthOrEvents = series.length === 0 && eventBands.length === 0 && !loading;
  const colorFor = React.useMemo(() => createColorLookup(DEFAULT_PALETTE), []);
  const { evtTip, clearEvtTip, handleMouseMoveCapture } = useEventTooltip(showEventBands, svgRef, xScale, features, w);

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', userSelect: 'none', pb: 1, position: 'relative' }}
      onMouseMoveCapture={handleMouseMoveCapture}
      onMouseLeave={clearEvtTip}
    >
      {caption ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 4,
            zIndex: 1,
            fontSize: 11,
            color: 'text.secondary',
            pointerEvents: 'none',
            '& .visx-area-closed': {
              // style={{ mixBlendMode: hi ? 'normal' : 'divide' }}
              mixBlendMode: 'multiply',
            },
          }}
        >
          {caption}
        </Box>
      ) : null}
      <svg ref={svgRef} width={w} height={h} role="img" aria-label="Mini depth chart" style={{ display: 'block', overflow: 'visible', transition: 'height 0.3s ease' }} shapeRendering="crispEdges">
        <ChartStatic
          w={w}
          h={h}
          xScale={xScale}
          yScale={yScale}
          yMax={yMax}
          unit={unit}
          series={series}
          highlightedKey={highlightedKey}
          textColorPrimary={textColorPrimary}
          textColorSecondary={textColorSecondary}
          showEventBands={showEventBands}
          themeMode={theme.palette.mode}
          bars={bars}
          precipMax={precipMax}
          eventBands={eventBands}
          tidePts={tidePts}
          onClick={onClick}
          colorFor={colorFor}
          hideYAxis={noDepthOrEvents}
        />
        <ChartOverlay
          w={w}
          h={h}
          xScale={xScale}
          yScale={yScale}
          currentTime={currentTime}
          from={fromTime}
          to={toTime}
          textColorPrimary={textColorPrimary}
          series={series}
          onClick={onClick}
          overlayCaptureHandlers={overlayCaptureHandlers}
          overlayClickSuppressRef={overlayClickSuppressRef}
        />
      </svg>
      {showEventBands && evtTip && (
        <Box sx={{ position: 'absolute', left: Math.min(Math.max(0, evtTip.x + 12), w - 180), top: 6, zIndex: 2, pointerEvents: 'none' }}>
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', px: 1, py: 0.5, borderRadius: 1, boxShadow: 3, fontSize: 12 }}>
            {evtTip.items.map((it, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                <b>{it.depth.toFixed(1)}"</b>
              </div>
            ))}
          </Box>
        </Box>
      )}
      {noDepthOrEvents && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          No flooding detected between
          <div>
            {fromTimeStr} and {toTimeStr}
          </div>
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          borderRadius: 0.5,
          opacity: showLoading ? 1 : 0,
          transition: 'opacity 220ms ease',
          pointerEvents: showLoading ? 'auto' : 'none',
        }}
      >
        <CircularProgress size={16} thickness={5} />
      </Box>
    </Box>
  )
}

export default React.memo(MiniDepthChart);

type ChartStaticProps = {
  w: number;
  h: number;
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
  yMax: number;
  unit: string;
  series: Series[];
  highlightedKey?: string | null;
  textColorPrimary: string;
  textColorSecondary: string;
  showEventBands: boolean;
  themeMode: 'light' | 'dark';
  bars: Array<{ x: number; width: number; height: number }>;
  precipMax?: number;
  eventBands: Array<{ x: number; width: number; height: number; color: string }>;
  tidePts: MiniPoint[];
  onClick?: (f: GeoJsonFeature) => void;
  colorFor: (key: string, i: number) => string;
  hideYAxis?: boolean;
}

const UNCONFIRMED_COLOR = 'rgba(178, 112, 45, 0.746)';

const ChartStatic = React.memo(function ChartStatic({ w, h, xScale, yScale, yMax, unit, series, highlightedKey, textColorPrimary, textColorSecondary, showEventBands, themeMode, bars, precipMax, eventBands, tidePts, onClick, colorFor, hideYAxis }: ChartStaticProps) {
  const orderedSeries = React.useMemo(() => {
    if (!highlightedKey) return series;
    const idx = series.findIndex((s) => s.key && s.key === highlightedKey);
    if (idx === -1) return series;
    const arr = [...series];
    const [hi] = arr.splice(idx, 1);
    arr.push(hi);
    return arr;
  }, [series, highlightedKey]);

  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const areaBlendMode = darkMode ? 'multiply' : 'screen';

  return (
    <>
      {bars.length > 0 && (
        <g>
          {bars.map((b, i) => (
            <rect key={i} x={b.x} y={h - 1 - b.height} width={b.width} height={b.height} fill="rgba(0, 132, 255, 0.344)" />
          ))}
        </g>
      )}
      {showEventBands && eventBands.length > 0 && (
        <g>
          {eventBands.map((b, i) => (
            <rect key={`evt-${i}`} x={b.x} y={h - b.height} width={b.width} height={b.height} rx={2} fill={b.color} />
          ))}
        </g>
      )}
      {tidePts.length > 1 && (
        <Group>
          <LinePath
            data={tidePts}
            x={(pt) => xScale(pt.t) ?? 0}
            y={(pt) => yScale(Math.min(yMax, pt.d)) ?? 0}
            stroke={themeMode === 'dark' ? 'rgba(0, 200, 255, 0.8)' : 'rgba(0, 120, 200, 0.8)'}
            strokeWidth={1}
            strokeDasharray="3,2"
            shapeRendering="crispEdges"
            fill="none"
            curve={curveLinear}
          />
        </Group>
      )}
      <AxisBottom
        top={h - 1} scale={xScale} numTicks={5}
        tickLength={0}
        stroke={textColorSecondary}
        tickStroke={textColorSecondary}
        tickLabelProps={() => ({ fontSize: 10, textAnchor: 'middle', fill: textColorPrimary })}
      />
      {!hideYAxis && (
        <g style={{ pointerEvents: 'none' }}>
          <AxisRight
            left={w - 1}
            scale={yScale}
            tickValues={[yMax]}
            tickFormat={() => {
              const depthLabel = `Depth max: ${yMax.toFixed(1)} ${unit}`;
              const precipLabel = precipMax ? ` • Precip max: ${precipMax.toFixed(2)} in` : '';
              return depthLabel + precipLabel;
            }}
            hideAxisLine
            tickLength={-6}
            stroke={textColorSecondary}
            tickStroke={textColorSecondary}
            tickLabelProps={() => ({ dx: -4, dy: '0.32em', fontSize: 10, textAnchor: 'end', fill: textColorPrimary })}
          />
        </g>
      )}
      <Group>
        {orderedSeries.map((s, i) => {
          const hi = highlightedKey && s.key && highlightedKey === s.key;
          const segs: MiniPoint[][] = [];
          let seg: MiniPoint[] = [];
          let lastLabel: string | undefined;
          for (let j = 0; j < s.data.length; j++) {
            const pt = s.data[j];
            const label = pt.label ?? (s.feature as any)?.properties?.label;
            if (seg.length && (+pt.t - +seg[seg.length - 1].t) > MAX_GAP_MS) {
              if (seg.length > 1) segs.push(seg);
              seg = [];
              lastLabel = undefined;
            }
            if (seg.length && label !== lastLabel) {
              if (seg.length > 1) segs.push(seg);
              seg = [];
            }
            if (!seg.length) lastLabel = label;
            seg.push(pt);
          }
          if (seg.length > 1) segs.push(seg);
          return (
            <g key={s.key || i} style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={() => onClick?.(s.feature)}>
              {segs.map((segPts, si) => (
                <React.Fragment key={`${s.key || i}-${si}`}>
                  {(() => {
                    const segLabel = segPts[0]?.label ?? (s.feature as any)?.properties?.label;
                    const segColor = segLabel === 'flood' ? colorFor(s.key, i) : UNCONFIRMED_COLOR;
                    const strokeColor = hi ? 'white' : segColor;
                    return (
                      <>
                        <AreaClosed
                          data={segPts}
                          x={(pt) => xScale(pt.t) ?? 0}
                          y={(pt) => yScale(pt.d) ?? 0}
                          y0={() => yScale(0)}
                          fill={segColor}
                          fillOpacity={0.2}
                          stroke="none"
                          style={{ mixBlendMode: hi ? 'normal' : areaBlendMode }}
                          curve={curveLinear}
                        />
                        <LinePath
                          data={segPts}
                          x={(pt) => xScale(pt.t) ?? 0}
                          y={(pt) => yScale(pt.d) ?? 0}
                          stroke={strokeColor}
                          strokeWidth={hi ? 2.8 : 1.1}
                          shapeRendering="crispEdges"
                          fill="none"
                          curve={curveLinear}
                          opacity={highlightedKey ? (hi ? 1 : 0.75) : 1}
                          style={{ zIndex: hi ? 2 : 1 }}
                        />
                      </>
                    )
                  })()}
                </React.Fragment>
              ))}
            </g>
          )
        })}
      </Group>
    </>
  )
});

type ChartOverlayProps = {
  w: number;
  h: number;
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
  currentTime?: Date;
  from: Date;
  to: Date;
  textColorPrimary: string;
  series: Series[];
  onClick?: (f: GeoJsonFeature) => void;
  overlayCaptureHandlers?: { onMouseDownCapture?: (e: React.MouseEvent<SVGRectElement>) => void; onMouseMoveCapture?: (e: React.MouseEvent<SVGRectElement>) => void; onMouseUpCapture?: (e: React.MouseEvent<SVGRectElement>) => void };
  overlayClickSuppressRef?: React.MutableRefObject<boolean | undefined>;
}

const ChartOverlay = React.memo(function ChartOverlay({ w, h, xScale, yScale, currentTime, from, to, textColorPrimary, series, onClick, overlayCaptureHandlers, overlayClickSuppressRef }: ChartOverlayProps) {
  const playheadX = React.useMemo(() => (currentTime && +currentTime >= +from && +currentTime <= +to) ? xScale(currentTime) : null, [currentTime, from, to, xScale]);
  return (
    <g>
      <HoverOverlay
        series={series}
        xScale={xScale}
        yScale={yScale}
        width={w}
        height={h}
        offsetLeft={0}
        offsetTop={0}
        onClick={onClick}
        onMouseDownCapture={overlayCaptureHandlers?.onMouseDownCapture}
        onMouseMoveCapture={overlayCaptureHandlers?.onMouseMoveCapture}
        onMouseUpCapture={overlayCaptureHandlers?.onMouseUpCapture}
        cancelClickRef={overlayClickSuppressRef}
      />
      {playheadX != null && (
        <line x1={playheadX} x2={playheadX} y1={2} y2={h - 2} stroke={textColorPrimary} strokeWidth={1} strokeDasharray="1,2" />
      )}
    </g>
  )
});

function useChartWidth() {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(400);
  React.useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(Math.max(MIN_CHART_WIDTH, Math.floor(e.contentRect.width)));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return { containerRef: ref, width };
}

function useSeries(features: GeoJsonFeature[], from: Date, to: Date): Series[] {
  return React.useMemo(() => {
    return (features || []).map((feature) => {
      const times = getTimes(feature);
      const depths = getDepths(feature);
      const labels = getLabels(feature);
      const n = Math.min(times.length, depths.length);
      const data: MiniPoint[] = [];
      for (let i = 0; i < n; i++) {
        const t = new Date(times[i]);
        if (+t < +from || +t > +to) continue;
        const label = Array.isArray(labels) ? labels[i] : labels;
        data.push({ t, d: depths[i] ?? 0, label });
      }
      return { key: getDeploymentId(feature), label: getLabel(feature), data: data.sort((a, b) => +a.t - +b.t), feature };
    }).filter((s) => s.data.length > 0);
  }, [features, from, to]);
}

function useYMax(series: Series[], features: GeoJsonFeature[], depthSoftMinInches: number, showEventBands: boolean) {
  return React.useMemo(() => {
    const seriesMax = series.reduce((maxVal, s) => Math.max(maxVal, ...s.data.map((pt) => pt.d)), 1);
    const eventMax = showEventBands
      ? (features || []).reduce((m, f) => Math.max(m, ...getPastEvents(f).map((e: any) => e.properties?.max_depth_inches ?? 0)), 1)
      : 1;
    return Math.max(showEventBands ? eventMax : seriesMax, depthSoftMinInches);
  }, [series, features, depthSoftMinInches, showEventBands]);
}

function useTidePoints(tide: any, from: Date, to: Date): MiniPoint[] {
  return React.useMemo(() => {
    const arr = (tide || []) as any[];
    if (!arr?.length) return [];
    return arr
      .map((f) => {
        const p = f.properties || {};
        const t = new Date(p.time);
        const navd88 = p.navd88_feet;
        const mllw = p.mllw_offset_feet;
        const mhhw = p.mhhw_offset_feet;
        const spanFeet = Math.max(1e-6, mhhw - mllw);
        const relFeet = Math.min(Math.max(0, navd88 - mllw), spanFeet);
        return { t, d: relFeet * 12 };
      })
      .filter((p) => +p.t >= +from && +p.t <= +to)
      .sort((a, b) => +a.t - +b.t);
  }, [tide, from, to]);
}

function usePrecipBars(precip: MaxPrecipDataFeatureProperties[] | undefined, from: Date, to: Date, xScale: ReturnType<typeof scaleTime<number>>, chartHeight: number) {
  return React.useMemo(() => {
    const arr = precip || [];
    if (!arr.length) return { bars: [] as Array<{ x: number; width: number; height: number }>, precipMax: undefined as number | undefined };
    const pts = arr
      .map((f) => ({ t: new Date(f.time as any), v: f.precip_in_inches_5min ?? 0 }))
      .filter((d) => +d.t >= +from && +d.t <= +to)
      .sort((a, b) => +a.t - +b.t);
    if (!pts.length) return { bars: [], precipMax: undefined };
    const deltas: number[] = [];
    for (let i = 1; i < pts.length; i++) deltas.push(+pts[i].t - +pts[i - 1].t);
    const med = deltas.length ? deltas.sort((a, b) => a - b)[Math.floor(deltas.length / 2)] : 5 * 60 * 1000;
    const pxW = Math.max(1, (xScale(new Date(+from + med)) - xScale(from)) * PRECIP_BAR_PADDING);
    const precipMaxVal = pts.reduce((m, d) => (d.v > m ? d.v : m), 0);
    const precipScaleMax = Math.max(precipMaxVal, PRECIP_SOFT_MIN);
    return {
      bars: pts.map((d) => {
        const height = (d.v / precipScaleMax) * (chartHeight - 4);
        return { x: xScale(d.t), width: pxW, height };
      }),
      precipMax: precipScaleMax,
    };
  }, [precip, from, to, xScale, chartHeight]);
}

function useEventBands(showEventBands: boolean, features: GeoJsonFeature[], from: Date, to: Date, xScale: ReturnType<typeof scaleTime<number>>, yScale: ReturnType<typeof scaleLinear<number>>) {
  return React.useMemo(() => {
    if (!showEventBands) return [];
    const out: Array<{ x: number; width: number; height: number; color: string }> = [];
    for (const f of features || []) {
      for (const e of getPastEvents(f)) {
        const ps = e.properties?.start_time instanceof Date ? e.properties.start_time : new Date(e.properties?.start_time);
        const pe = e.properties?.end_time instanceof Date ? e.properties.end_time : new Date(e.properties?.end_time ?? e.properties?.start_time);
        if (!(ps instanceof Date) || !(pe instanceof Date) || isNaN(+ps) || isNaN(+pe)) continue;
        const s = new Date(Math.max(+from, +ps));
        const t = new Date(Math.min(+to, +pe));
        if (+t <= +s) continue;
        const x1 = xScale(s);
        const x2 = xScale(t);
        const width = Math.max(1, Math.abs(x2 - x1));
        const y1 = yScale(0);
        const y2 = yScale(e.properties?.max_depth_inches);
        const height = Math.abs(y2 - y1);
        const [r, g, b] = depthToColor(height);
        const color = `rgba(${r}, ${g}, ${b}, 0.28)`;
        out.push({ x: Math.min(x1, x2), width, height, color });
      }
    }
    return out.sort((a, b) => b.height - a.height);
  }, [showEventBands, features, from, to, xScale, yScale]);
}

function createColorLookup(palette: string[]) {
  const hash = (s: string) => s.split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
  return (key: string, i: number) => palette[Math.abs(key ? hash(key) : i) % palette.length];
}

function useEventTooltip(showEventBands: boolean, svgRef: React.RefObject<SVGSVGElement>, xScale: ReturnType<typeof scaleTime<number>>, features: GeoJsonFeature[], width: number) {
  const [evtTip, setEvtTip] = React.useState<{ x: number; items: Array<{ name: string; depth: number }> } | null>(null);
  const clearEvtTip = React.useCallback(() => setEvtTip(null), []);
  const handleMouseMoveCapture = React.useCallback((e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!showEventBands || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const localX = Math.max(0, Math.min(width, e.clientX - rect.left));
    const t = xScale.invert(localX);
    const items: Array<{ name: string; depth: number }> = [];
    for (const f of features || []) {
      for (const ev of getPastEvents(f)) {
        const s = new Date(ev.properties?.start_time);
        const ee = new Date(ev.properties?.end_time || ev.properties?.start_time);
        if (+t >= +s && +t <= +ee) {
          items.push({ name: getLabel(f), depth: ev.properties?.max_depth_inches ?? 0 });
        }
      }
    }
    if (items.length) setEvtTip({ x: localX, items: items.sort((a, b) => b.depth - a.depth).slice(0, 8) });
    else setEvtTip(null);
  }, [showEventBands, svgRef, width, xScale, features]);
  return { evtTip, clearEvtTip, handleMouseMoveCapture };
}
